import type { AuditChange, AuditEntry } from '@/api/generated';

import { actionPhrase } from './actions';
import { EMPTY, fieldLabel, formatValue, formatValueText } from './formatValue';

/**
 * One entry as one sentence: what happened, to whom, and - when it fits - the
 * value before and after.
 *
 * The table's What happened column and the account page's panel both read
 * this, so an entry is worded the same wherever it appears.
 *
 * `changes` is what this viewer may see, already filtered. The sentence is
 * built from nothing else, so the organization's own views cannot mention a
 * staff-only field even by name. With nothing visible to describe, the
 * sentence is the action phrase the drawer's heading uses.
 *
 * The organization is never named: on the staff page it has its own column,
 * and on the organization's own page it is always the organization reading.
 * A person is named, because the row has nowhere else to say who.
 */

/** Longest a before or after value may be and still go in the sentence. */
const SHORT = 40;

/** A value as the sentence would show it, or null when it is too long to. */
function short(value: unknown, field: string): string | null {
  const formatted = formatValue(value, field);
  if (formatted.kind !== 'text' || formatted.text.length > SHORT) return null;
  return formatted.text;
}

/** "before → after", or null when either side is too long. */
function values(change: AuditChange): string | null {
  const before = short(change.before, change.field);
  const after = short(change.after, change.field);
  if (before === null || after === null) return null;
  return `${before} → ${after}`;
}

/** "Field: before → after", or null when either side is too long. */
function beforeAfter(change: AuditChange): string | null {
  const both = values(change);
  return both === null ? null : `${fieldLabel(change.field)}: ${both}`;
}

/** "A", "A and B", "A, B and C", or "A, B and 3 more". */
function fieldList(changes: AuditChange[]): string {
  const names = changes.map((change) => fieldLabel(change.field));
  if (names.length <= 3) {
    return names.length === 1
      ? names[0]
      : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
  }
  return `${names.slice(0, 2).join(', ')} and ${names.length - 2} more`;
}

/** One short change in full, or the fields by name after `lead`. */
function fieldsSentence(changes: AuditChange[], lead: string): string | null {
  if (changes.length === 0) return null;
  if (changes.length === 1) {
    const single = beforeAfter(changes[0]);
    if (single) return single;
  }
  return `${lead} ${fieldList(changes)}`;
}

function find(changes: AuditChange[], field: string): AuditChange | undefined {
  return changes.find((change) => change.field === field);
}

/** The role, in words, from the side of the `role` change asked for. */
function role(changes: AuditChange[], side: 'before' | 'after'): string | null {
  const change = find(changes, 'role');
  const value = change?.[side];
  if (value === null || value === undefined || value === '') return null;
  return formatValueText(value, 'role');
}

/** The person an entry is about, when it is about a person. */
function person(entry: AuditEntry): string | null {
  if (entry.target.type !== 'portal_user') return null;
  return entry.target.label || null;
}

/** "Added the logo", "Removed the cover image", from presence before and after. */
function imageSentence(changes: AuditChange[], noun: string): string | null {
  const change = changes[0];
  if (!change) return null;
  const had = change.before !== null && change.before !== undefined;
  const has = change.after !== null && change.after !== undefined;
  if (!had && has) return `Added the ${noun}`;
  if (had && !has) return `Removed the ${noun}`;
  if (had && has) return `Replaced the ${noun}`;
  return null;
}

function billingContactSentence(changes: AuditChange[]): string | null {
  const change = find(changes, 'billing_contact');
  if (!change) return null;
  const after = change.after;
  const name =
    after && typeof after === 'object' && 'name' in after
      ? (after as { name?: unknown }).name
      : null;
  if (typeof name === 'string' && name.trim()) {
    return `Changed the billing contact to ${name.trim()}`;
  }
  if (formatValueText(after, change.field) === EMPTY)
    return 'Removed the billing contact';
  return null;
}

/** " as Admin", or nothing. */
const as = (value: string | null) => (value ? ` as ${value}` : '');
/** " (Admin)", or nothing. */
const inBrackets = (value: string | null) => (value ? ` (${value})` : '');

function sentence(entry: AuditEntry, changes: AuditChange[]): string | null {
  const who = person(entry);

  switch (entry.action) {
    case 'organization.created':
      return 'Created the organization';
    case 'organization.updated':
      return fieldsSentence(changes, 'Updated');
    case 'organization.status_changed': {
      const status = find(changes, 'status');
      return status ? beforeAfter(status) : null;
    }
    case 'organization.profile_updated':
      return fieldsSentence(changes, 'Updated the profile:');
    case 'organization.logo_updated':
      return imageSentence(changes, 'logo');
    case 'organization.cover_updated':
      return imageSentence(changes, 'cover image');
    case 'organization.billing_contact_updated':
      return billingContactSentence(changes);
    case 'invitation.sent':
      return who ? `Invited ${who}${as(role(changes, 'after'))}` : null;
    case 'invitation.resent':
      return who
        ? `Resent ${who}’s invitation${inBrackets(role(changes, 'after'))}`
        : null;
    case 'invitation.revoked':
      return who
        ? `Revoked ${who}’s invitation${inBrackets(role(changes, 'before'))}`
        : null;
    case 'invitation.accepted': {
      const joinedAs = as(role(changes, 'after'));
      // The invitee accepts for themselves; nobody else is signed in when a
      // link is used. Naming them again would say the same person twice.
      if (entry.actor.id && entry.actor.id === entry.target.id) {
        return `Accepted their invitation and joined${joinedAs}`;
      }
      return who ? `${who} accepted an invitation${joinedAs}` : null;
    }
    case 'user.role_changed': {
      if (!who) return null;
      const change = find(changes, 'role');
      const both = change ? values(change) : null;
      return both ? `Changed ${who}’s role: ${both}` : `Changed ${who}’s role`;
    }
    case 'user.removed':
      return who ? `Removed ${who}` : null;
    default:
      // A code the portal has no words for, shown as it came, with what it
      // was about when that is something other than the organization.
      return entry.target.label && entry.target.type !== 'organization'
        ? `${entry.action} · ${entry.target.label}`
        : entry.action;
  }
}

export function summarizeEntry(entry: AuditEntry, changes: AuditChange[]): string {
  return sentence(entry, changes) ?? actionPhrase(entry.action);
}
